import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import { FaFacebookF, FaInstagram, FaTwitter, FaLinkedinIn, FaWhatsapp } from 'react-icons/fa';

// Kategoriler
const CATEGORIES = [
  { id: 'deck', name: 'Deck' },
  { id: 'fence', name: 'Çit Sistemleri' },
  { id: 'garden_gate', name: 'Bahçe Kapı Sistemleri' },
  { id: 'facade', name: 'Cephe Sistemleri-Vinylbond' },
  { id: 'pergola', name: 'Pergola' },
  { id: 'baffle_ceiling', name: 'Baffle Tavan / Seperatör' },
  { id: 'flat_panel', name: 'Flat Paneller' },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isHome, setIsHome] = useState(false);
  const [showProductsDropdown, setShowProductsDropdown] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsHome(location.pathname === '/');
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > (window.innerHeight * 0.8);
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Ana Sayfa', path: '/' },
    { name: 'Hakkımızda', path: '/about' },
    {
      name: 'Ürünler',
      path: '/products',
      hasDropdown: true,
      dropdownItems: CATEGORIES.map(cat => ({
        name: cat.name,
        path: `/products?category=${cat.id}`
      }))
    },
    { name: 'Projeler', path: '/projects' },
    { name: 'Galeri', path: '/gallery' },
    { name: 'Blog', path: '/blogs' },
    { name: 'İletişim', path: '/contact' },
  ];

  const socialLinks = [
    { Icon: FaFacebookF, url: 'https://www.facebook.com/DETAYDECKK/', label: 'Facebook' },
    { Icon: FaInstagram, url: 'https://www.instagram.com/detaydeck/', label: 'Instagram' },
    { Icon: FaTwitter, url: 'https://x.com/detaydeck', label: 'Twitter' },
    { Icon: FaLinkedinIn, url: 'https://linkedin.com/in/detay-deck-534753210', label: 'LinkedIn' },
    { Icon: FaWhatsapp, url: 'https://wa.me/905387416525', label: 'WhatsApp' }
  ];

  const shouldShowNav = !isHome || scrolled;

  return (
    <>
      {/* Ana Navbar */}
      <AnimatePresence>
        {shouldShowNav && (
          <motion.nav
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center h-16">
                {/* Logo */}
                <motion.div
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="flex-shrink-0"
                >
                  <Link to="/">
                    <img
                      src="/images/logos/pc-logo.png"
                      alt="DetayDeck"
                      className="h-8 hidden md:block"
                    />
                    <img
                      src="/images/logos/mobil-logo.png"
                      alt="DetayDeck"
                      className="h-10 md:hidden"
                    />
                  </Link>
                </motion.div>

                {/* Desktop Menu */}
                <motion.div
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="hidden md:flex items-center justify-center flex-grow"
                >
                  <div className="flex items-center space-x-6">
                    {navLinks.map((link, index) => (
                      <motion.div
                        key={link.path}
                        initial={{ y: -20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        className="relative group flex items-center h-16"
                      >
                        {link.hasDropdown ? (
                          <div
                            className="relative"
                            onMouseEnter={() => setShowProductsDropdown(true)}
                            onMouseLeave={() => setShowProductsDropdown(false)}
                          >
                            <button
                              className="flex items-center text-gray-600 hover:text-[#dbc582] transition-colors text-sm font-medium"
                            >
                              {link.name}
                              <ChevronDown className="ml-1 h-4 w-4" />
                            </button>
                            <AnimatePresence>
                              {showProductsDropdown && (
                                <motion.div
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: 10 }}
                                  transition={{ duration: 0.2 }}
                                  className="absolute left-0 top-10 -mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 before:content-[''] before:absolute before:w-full before:h-4 before:-top-4"
                                >
                                  <div className="py-1">
                                    <Link
                                      to="/products"
                                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                      onClick={() => setShowProductsDropdown(false)}
                                    >
                                      Tüm Ürünler
                                    </Link>
                                    {link.dropdownItems.map((item) => (
                                      <Link
                                        key={item.path}
                                        to={item.path}
                                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        onClick={() => setShowProductsDropdown(false)}
                                      >
                                        {item.name}
                                      </Link>
                                    ))}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        ) : (
                          <Link
                            to={link.path}
                            className="text-gray-600 hover:text-[#dbc582] transition-colors text-sm font-medium"
                          >
                            {link.name}
                          </Link>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </motion.div>

                {/* Social Icons */}
                <motion.div
                  initial={{ x: 20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="hidden md:flex items-center space-x-4"
                >
                  {socialLinks.map(({ Icon, url, label }) => (
                    <a
                      key={label}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-[#dbc582] transition-colors"
                      aria-label={label}
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  ))}
                </motion.div>

                {/* Mobile menu button */}
                <motion.div
                  initial={{ x: 20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="md:hidden"
                >
                  <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none"
                  >
                    <span className="sr-only">Open main menu</span>
                    {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                  </button>
                </motion.div>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden fixed inset-x-0 top-16 z-50 bg-white shadow-lg"
          >
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navLinks.map((link) => (
                <div key={link.path}>
                  {link.hasDropdown ? (
                    <>
                      <button
                        onClick={() => setShowProductsDropdown(!showProductsDropdown)}
                        className="w-full text-left px-3 py-2 text-base font-medium text-gray-600 hover:text-[#dbc582] hover:bg-gray-50 flex items-center justify-between"
                      >
                        {link.name}
                        <ChevronDown className={`h-4 w-4 transform transition-transform ${showProductsDropdown ? 'rotate-180' : ''}`} />
                      </button>
                      <AnimatePresence>
                        {showProductsDropdown && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="pl-4"
                          >
                            <Link
                              to="/products"
                              className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-[#dbc582] hover:bg-gray-50"
                              onClick={() => setIsOpen(false)}
                            >
                              Tüm Ürünler
                            </Link>
                            {link.dropdownItems.map((item) => (
                              <Link
                                key={item.path}
                                to={item.path}
                                className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-[#dbc582] hover:bg-gray-50"
                                onClick={() => setIsOpen(false)}
                              >
                                {item.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <Link
                      to={link.path}
                      className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-[#dbc582] hover:bg-gray-50"
                      onClick={() => setIsOpen(false)}
                    >
                      {link.name}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      {isHome && !scrolled && (
        <div className="absolute top-0 left-0 right-0 z-50">
          <div className="bg-[rgb(26,26,26)]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center h-16">
                {/* Logo */}
                <Link to="/" className="text-2xl font-light relative">
                  <div className="relative">
                    <img
                      src="/images/logos/pc-logo.png"
                      alt="DetayDeck"
                      className="h-8 hidden md:block brightness-0 invert"
                    />
                    <img
                      src="/images/logos/mobil-logo.png"
                      alt="DetayDeck"
                      className="h-10 md:hidden brightness-0 invert"
                    />
                  </div>
                </Link>

                {/* Desktop Menu */}
                <div className="hidden md:flex items-center space-x-6">
                  {navLinks.map((link, index) => (
                    <motion.div
                      key={link.path}
                      initial={{ y: -20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      className="relative group flex items-center h-16"
                    >
                      {link.hasDropdown ? (
                        <div
                          className="relative"
                          onMouseEnter={() => setShowProductsDropdown(true)}
                          onMouseLeave={() => setShowProductsDropdown(false)}
                        >
                          <button
                            className="flex items-center text-white hover:text-[#dbc582] transition-colors text-sm font-medium"
                          >
                            {link.name}
                            <ChevronDown className="ml-1 h-4 w-4" />
                          </button>
                          <AnimatePresence>
                            {showProductsDropdown && (
                              <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 10 }}
                                transition={{ duration: 0.2 }}
                                className="absolute left-0 top-10 -mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 before:content-[''] before:absolute before:w-full before:h-4 before:-top-4"
                              >
                                <div className="py-1">
                                  <Link
                                    to="/products"
                                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    onClick={() => setShowProductsDropdown(false)}
                                  >
                                    Tüm Ürünler
                                  </Link>
                                  {link.dropdownItems.map((item) => (
                                    <Link
                                      key={item.path}
                                      to={item.path}
                                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                      onClick={() => setShowProductsDropdown(false)}
                                    >
                                      {item.name}
                                    </Link>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ) : (
                        <Link
                          to={link.path}
                          className="text-white hover:text-[#dbc582] transition-colors text-sm font-medium"
                        >
                          {link.name}
                        </Link>
                      )}
                    </motion.div>
                  ))}
                </div>

                {/* Social Icons */}
                <div className="hidden md:flex items-center space-x-4">
                  {socialLinks.map(({ Icon, url, label }) => (
                    <a
                      key={label}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white hover:text-[#dbc582] transition-colors"
                      aria-label={label}
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  ))}
                </div>

                {/* Mobile menu button */}
                <div className="md:hidden">
                  <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="inline-flex items-center justify-center p-2 rounded-md text-white hover:text-[#dbc582] focus:outline-none"
                  >
                    <span className="sr-only">Open main menu</span>
                    {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Mobile Menu for Hero */}
            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="md:hidden bg-black/80 backdrop-blur-sm"
                >
                  <div className="px-2 pt-2 pb-3 space-y-1">
                    {navLinks.map((link) => (
                      <div key={link.path}>
                        {link.hasDropdown ? (
                          <>
                            <button
                              onClick={() => setShowProductsDropdown(!showProductsDropdown)}
                              className="w-full text-left px-3 py-2 text-base font-medium text-white hover:text-[#dbc582] hover:bg-white/10 flex items-center justify-between"
                            >
                              {link.name}
                              <ChevronDown className={`h-4 w-4 transform transition-transform ${showProductsDropdown ? 'rotate-180' : ''}`} />
                            </button>
                            <AnimatePresence>
                              {showProductsDropdown && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  className="pl-4"
                                >
                                  <Link
                                    to="/products"
                                    className="block px-3 py-2 text-base font-medium text-white hover:text-[#dbc582] hover:bg-white/10"
                                    onClick={() => setIsOpen(false)}
                                  >
                                    Tüm Ürünler
                                  </Link>
                                  {link.dropdownItems.map((item) => (
                                    <Link
                                      key={item.path}
                                      to={item.path}
                                      className="block px-3 py-2 text-base font-medium text-white hover:text-[#dbc582] hover:bg-white/10"
                                      onClick={() => setIsOpen(false)}
                                    >
                                      {item.name}
                                    </Link>
                                  ))}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </>
                        ) : (
                          <Link
                            to={link.path}
                            className="block px-3 py-2 text-base font-medium text-white hover:text-[#dbc582] hover:bg-white/10"
                            onClick={() => setIsOpen(false)}
                          >
                            {link.name}
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
